import { useMemo, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CloseIcon from "assets/icons/close.svg?react";
import budgetService, {
  IUpdateExpenseQuotaRequest,
} from "api/budgetService";
import { budgetDialogPaperProps } from "components/Budget/budgetDialog";
import { IBudgetExpense } from "dto/Budget/IBudgetExpense";
import { IBudgetParagraph } from "dto/Budget/IBudgetDictionaries";
import { formatMoney } from "utils/formatMoney";
import { ENotificationType, notification } from "utils/notification";
import { randomString } from "utils/randomString";
import styles from "./styles.module.scss";

interface Props {
  expense: IBudgetExpense;
  onClose: () => void;
  onUpdated?: () => void;
}

interface EditRow {
  key: string;
  quotaId?: string;
  paragraphId: string;
  suffix: string;
  amount: string;
}

const suffixes = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

const EditBudgetExpenseModal = ({ expense, onClose, onUpdated }: Props) => {
  const [title, setTitle] = useState(expense.Title);
  const [sharedTask, setSharedTask] = useState(!!expense.SharedTask);
  const [rows, setRows] = useState<EditRow[]>(
    expense.Quotas.map((q) => ({
      key: q.Id,
      quotaId: q.Id,
      paragraphId: q.ParagraphId,
      suffix: q.ParagraphSuffix || "0",
      amount: String(q.Amount),
    })),
  );

  const { data: paragraphs = [] } = budgetService.useGetParagraphs();
  const { data: engagements = [] } = budgetService.useGetEngagements(expense.Id);
  const { mutate: updateExpense, isLoading } = budgetService.useUpdateExpense();

  // Suma zaangażowana per kwota wydatku — blokuje usunięcie / zejście poniżej.
  const engagedByQuota = useMemo(() => {
    const map: Record<string, number> = {};
    engagements
      .flatMap((e) => e.Quotas)
      .forEach((q) => {
        map[q.ExpenseQuotaId] = (map[q.ExpenseQuotaId] ?? 0) + q.Amount;
      });
    return map;
  }, [engagements]);

  const paragraphLabel = (paragraphId: string) => {
    const p = paragraphs.find((x) => x.Id === paragraphId);
    return p ? `${p.Symbol} – ${p.Name}` : "—";
  };

  const total = useMemo(
    () => rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0),
    [rows],
  );

  const updateRow = (key: string, patch: Partial<EditRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      { key: randomString(), paragraphId: "", suffix: "0", amount: "" },
    ]);

  const removeRow = (key: string) =>
    setRows((prev) => prev.filter((r) => r.key !== key));

  const validRows = rows.filter(
    (r) => r.paragraphId && (parseFloat(r.amount) || 0) >= 0,
  );
  const canSubmit =
    !!title.trim() &&
    validRows.length > 0 &&
    validRows.some((r) => (parseFloat(r.amount) || 0) > 0);

  const handleSubmit = () => {
    if (!canSubmit) return;
    const quotas: IUpdateExpenseQuotaRequest[] = validRows.map((r) => ({
      Id: r.quotaId,
      ParagraphId: r.paragraphId,
      ParagraphSuffix: r.suffix,
      Amount: parseFloat(r.amount) || 0,
    }));

    updateExpense(
      { ExpenseId: expense.Id, Title: title.trim(), SharedTask: sharedTask, Quotas: quotas },
      {
        onSuccess: () => {
          onUpdated?.();
          onClose();
        },
        onError: (err) => {
          const message =
            (err as { response?: { data?: { Message?: string } } })?.response
              ?.data?.Message ?? "Nie udało się zapisać zmian w wydatku.";
          notification({
            title: "Nie udało się zapisać",
            text: message,
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={budgetDialogPaperProps}
    >
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <span className={styles.title}>Edycja wydatku</span>
          <IconButton onClick={onClose} size="small" className={styles.close}>
            <CloseIcon className={styles.closeIcon} />
          </IconButton>
        </div>

        {expense.ChapterSymbol && (
          <div className={styles.classification}>Rozdział {expense.ChapterSymbol}</div>
        )}

        <div className={styles.content}>
          <TextField
            label="Tytuł"
            size="small"
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={sharedTask}
                onChange={(e) => setSharedTask(e.target.checked)}
              />
            }
            label="Zadanie wspólne"
          />

          <div className={styles.paragraphsHeader}>
            <span className={styles.sectionTitle}>Paragrafy</span>
            <IconButton onClick={addRow} size="small" color="primary">
              +
            </IconButton>
          </div>

          <div className={styles.gridHead}>
            <span>Paragraf</span>
            <span>4.ta cyfra §</span>
            <span>Kwota</span>
            <span />
          </div>

          {rows.map((row) => {
            const engaged = row.quotaId ? engagedByQuota[row.quotaId] ?? 0 : 0;
            const isExisting = !!row.quotaId;
            const entered = parseFloat(row.amount) || 0;
            const belowEngaged = isExisting && entered < engaged;
            return (
              <div className={styles.row} key={row.key}>
                {isExisting ? (
                  <div className={styles.paragraphCell}>
                    <span className={styles.paragraphLabel}>
                      {paragraphLabel(row.paragraphId)}
                    </span>
                    {engaged > 0 && (
                      <span className={styles.engaged}>
                        zaangażowano {formatMoney(engaged)}
                      </span>
                    )}
                  </div>
                ) : (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={paragraphs}
                    value={paragraphs.find((p) => p.Id === row.paragraphId) ?? null}
                    onChange={(_, val: IBudgetParagraph | null) =>
                      updateRow(row.key, { paragraphId: val?.Id ?? "" })
                    }
                    getOptionLabel={(p) => `${p.Symbol} – ${p.Name}`}
                    isOptionEqualToValue={(o, v) => o.Id === v.Id}
                    openOnFocus
                    noOptionsText="Brak paragrafów"
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder="Wybierz paragraf" />
                    )}
                  />
                )}

                <FormControl size="small">
                  <Select
                    value={row.suffix}
                    onChange={(e) => updateRow(row.key, { suffix: e.target.value })}
                  >
                    {suffixes.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  error={belowEngaged}
                  helperText={
                    belowEngaged ? `Min. ${formatMoney(engaged)} (zaangażowano)` : undefined
                  }
                  value={row.amount}
                  onChange={(e) => updateRow(row.key, { amount: e.target.value })}
                />

                <IconButton
                  onClick={() => removeRow(row.key)}
                  size="small"
                  disabled={engaged > 0}
                  title={
                    engaged > 0
                      ? "Nie można usunąć — paragraf ma zaangażowania"
                      : "Usuń paragraf"
                  }
                >
                  ×
                </IconButton>
              </div>
            );
          })}

          <div className={styles.total}>
            <span>Budżet zadania</span>
            <strong>{formatMoney(total)}</strong>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            className={styles.submit}
            disabled={!canSubmit || isLoading}
            onClick={handleSubmit}
          >
            Zapisz
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default EditBudgetExpenseModal;
